// The Player's Web Audio path for MIDI songs (issue #3718): the song's notes scheduled on the page's
// one AudioContext from the soundfont, with the page audio session declared so iOS plays through
// the silent switch and keeps playing behind a locked phone or a minimised app. Chosen by the
// "Web Audio" song option; off, the Player renders the song to a WAV and plays it through media
// elements as it has for years (midi_file_player.ts), with the pauses on every tempo and mixer
// change that path cannot avoid.
//
// What lives where:
//   - When each note sounds is web_audio_song_scheduler.ts: tempo is its rate, verses its loop, and
//     it is unit-tested under a fake clock. This file is its sink: it turns a note into buffer
//     source and gain nodes on the context, and its "at" wakes into a silent source whose `ended`
//     event fires from the audio clock, so nothing here depends on a timer behind a locked phone
//     (a timer calls fill() too, as a backstop that costs nothing when the stretch ahead is full).
//   - How loud each part is: one gain node per voice, set from the mixer's voice values, changed
//     in place (setTargetAtTime over 20 ms) so a mixer change interrupts nothing. The parts sum
//     into a master gain, a compressor used as a limiter and a ceiling trim (the gain staging
//     below, issue #4003), which is what keeps four parts at full volume from clipping where the
//     MP3 path normalises the rendered file to a peak just under full scale.
//   - What the page declares to the operating system: the page audio session
//     (client/src/audio/audio_session.ts), activated synchronously inside the tap that plays, before
//     the context is resumed, and rested on pause. On iOS 15 and 16 that session plays a looping
//     silent element in place of the Audio Session API.
//   - The lock-screen and headphone controls: the Sequencer sets the Media Session metadata and
//     action handlers while a song plays, as it always has; whether WebKit surfaces Now Playing for
//     Web Audio alone was never measured (the issue's open question), so while a song plays this
//     player also plays a looping silent media element, which is known to carry Now Playing on iOS
//     and the media notification on Android, and pauses it with the song. The Media Session
//     playback state is set alongside.
//
// The context is never closed and never suspended here (client/src/audio/page_audio_context.ts has
// why); a pause fades the notes sounding and cancels the rest.

import autoBind from 'auto-bind';
import { MidiData, MidiNoteOffEvent, MidiNoteOnEvent, parseMidi } from 'midi-file';
import { getFirstNoteMidi } from '../../../common/src/midi_utils.ts';
import { getVoiceValue, Voice, VoiceValues } from '../../../common/src/model.ts';
import { createSilentAudioElement, getPageAudioSession, PageAudioSession } from '../audio/audio_session.ts';
import { getPageAudioContext } from '../audio/page_audio_context.ts';
import { NetworkError } from '../util/network.ts';
import { AudioPlayer } from './audio_player.ts';
import { getMidiNoteName, getSoundfontBuffer, loadInstrument } from './instrument.ts';
import { getMidiSong, MidiNote, MidiSong } from './midi_notes.ts';
import {
  NowPlayingPosition,
  ScheduledNoteHandle,
  SongSink,
  WebAudioSongScheduler,
} from './web_audio_song_scheduler.ts';

// Gain staging (issue #4003). Four fortissimo samples summed peak about +8 dBFS, so the master
// puts a full chord a few dB over the limiter's threshold and the limiter catches the attacks
// alone (over 3 dB of reduction about 5% of the time on the densest hymns, measured on an iPhone);
// a master of 0.8 kept the limiter working constantly and still let chord attacks clip, because
// the compressor node adds makeup gain of its own. The ceiling trim after the limiter puts the
// loudest chord at -1 dBTP, the headroom a Bluetooth codec needs.
const masterGain = 0.6;
const ceilingGain = 0.93;
const limiterSettings = { threshold: -6, knee: 6, ratio: 20, attack: 0, release: 0.15 };
// The samples were trimmed inside the attack and start on a step (up to -19 dBFS): a short ramp
// in removes the tick without touching the piano's own attack. The MP3 path's recordNote has it too.
const noteAttackSec = 0.004;
const releaseSec = 1 / 8; // a note's fade once it is let go: the MP3 path's recordNote fade, stopNote's ramp
// The bass samples end while still ringing, so a note held past its sample is let go this far
// before the sample ends and fades instead of cutting (the MP3 path's recordNote does the same)
const sampleEndMarginSec = 0.02;
const stopFadeSec = 0.05; // a sounding note cut by a pause or a seek fades over this rather than popping
const voiceGainSmoothingSec = 0.02;
const envelopeFloor = 0.0001; // exponential ramps cannot reach 0
const firstNoteTempo = 100; // the first-note preview plays at this tempo, as the MP3 first-note player does
const backstopIntervalMs = 1000;
// A tempo change re-schedules the whole stretch ahead (a few hundred nodes), so a slider moving is
// followed ten times a second, which the ear takes for at once
const tempoApplyIntervalMs = 100;

export interface MidiFilePlayerWebAudioOptions {
  audioContext?: () => AudioContext;
  audioSession?: PageAudioSession;
}

// The silent element that carries Now Playing while a song plays through Web Audio: one for the
// page, made the first time a song plays
let nowPlayingElement: HTMLAudioElement | undefined;

function playNowPlayingElement() {
  nowPlayingElement ??= createSilentAudioElement();
  if (!nowPlayingElement.paused) {
    return;
  }
  // an element the browser refuses (a play that lands outside a gesture) leaves the lock screen
  // without controls, which is where it was before the element
  try {
    nowPlayingElement.play().catch(() => undefined);
  } catch {
    // a browser that throws rather than rejects: the same refusal
  }
}

function pauseNowPlayingElement() {
  if (nowPlayingElement && !nowPlayingElement.paused) {
    nowPlayingElement.pause();
  }
}

// A second stop() moves the stop time on current browsers; an older WebKit threw on it instead
function stopSource(source: AudioBufferSourceNode, whenSec?: number) {
  try {
    source.stop(whenSec);
  } catch {
    // already stopped
  }
}

function setMediaSessionPlaybackState(state: MediaSessionPlaybackState) {
  if ('mediaSession' in navigator) {
    navigator.mediaSession.playbackState = state;
  }
}

// The Now Playing slider (issue #4049). With only the looping one-second silent element to go on,
// iOS showed every song as a second long, so the verse's length and position at the current tempo
// are reported through the Media Session API, from which the OS carries the position on at the
// rate given while the playback state is 'playing'. The slider is cosmetic: a browser without the
// call, or one that refuses the state, must never cost the play that reports it.
function setMediaSessionPosition(position: NowPlayingPosition | undefined) {
  if (!('mediaSession' in navigator) || typeof navigator.mediaSession.setPositionState !== 'function') {
    return;
  }
  try {
    if (!position) {
      navigator.mediaSession.setPositionState();
      return;
    }
    navigator.mediaSession.setPositionState({
      duration: position.durationSec,
      position: position.positionSec,
      playbackRate: 1,
    });
  } catch (error: unknown) {
    console.log('Media Session position state refused', error);
  }
}

export class MidiFilePlayerWebAudio implements AudioPlayer {
  initialTime = '0:00';
  playing = false;
  readonly appliesChangesLive = true;
  private readonly midiFilePath: string;
  private readonly setSongReady: () => void;
  private readonly onNetworkError: (networkError: NetworkError) => void;
  private readonly onVoiceChange?: (voices: Voice[]) => void;
  private readonly endOfFile: () => void;
  private readonly modulationHalfSteps: () => number;
  private readonly onDurationChange?: (duration: number | undefined) => void;
  private readonly getCurrentTempo: () => number | undefined;
  private readonly updateLocationSlider?: () => void;
  private readonly getVoiceValues: () => VoiceValues;
  private readonly getAudioContext: () => AudioContext;
  private readonly audioSession: PageAudioSession;
  private readonly scheduler: WebAudioSongScheduler;
  private midiData: MidiData | undefined;
  private song: MidiSong | undefined;
  private destroyed = false;
  // the transposition the notes are scheduled with; a change re-schedules what is still to come
  private modulation = 0;
  // the graph, made with the context on first use
  private context: AudioContext | undefined;
  private master: GainNode | undefined;
  private limiter: DynamicsCompressorNode | undefined;
  private ceiling: GainNode | undefined;
  private readonly voiceGains = new Map<Voice, GainNode>();
  private wakeBuffer: AudioBuffer | undefined;
  private firstNote: ScheduledNoteHandle | undefined;
  private backstop: ReturnType<typeof setInterval> | undefined;
  // A tempo change re-schedules the stretch ahead; a slider moving sends dozens a second, so they
  // are applied at most once per tempoApplyIntervalMs, the last one always landing
  private tempoApplyTimer: ReturnType<typeof setTimeout> | undefined;
  private tempoAppliedAtMs = -Infinity;

  constructor(
    {
      midiFilePath,
      setSongReady,
      onNetworkError,
      onVoiceChange,
      endOfFile,
      modulationHalfSteps,
      onDurationChange,
      getCurrentTempo,
      updateLocationSlider,
      getVoiceValues,
    }: {
      midiFilePath: string;
      setSongReady: () => void;
      onNetworkError: (networkError: NetworkError) => void;
      onVoiceChange?: (voices: Voice[]) => void;
      endOfFile: () => void;
      modulationHalfSteps: () => number;
      onDurationChange?: (duration: number | undefined) => void;
      getCurrentTempo: () => number | undefined;
      updateLocationSlider?: () => void;
      getVoiceValues: () => VoiceValues;
    },
    options: MidiFilePlayerWebAudioOptions = {},
  ) {
    autoBind(this);
    this.midiFilePath = midiFilePath;
    this.setSongReady = setSongReady;
    this.onNetworkError = onNetworkError;
    this.onVoiceChange = onVoiceChange;
    this.endOfFile = endOfFile;
    this.modulationHalfSteps = modulationHalfSteps;
    this.onDurationChange = onDurationChange;
    this.getCurrentTempo = getCurrentTempo;
    this.updateLocationSlider = updateLocationSlider;
    this.getVoiceValues = getVoiceValues;
    this.getAudioContext = options.audioContext ?? getPageAudioContext;
    this.audioSession = options.audioSession ?? getPageAudioSession();
    this.scheduler = new WebAudioSongScheduler(this.sink());
    this.scheduler.onVerseEnd = this.onVerseEnd;
    void this.load();
  }

  // ---- AudioPlayer ----------------------------------------------------------------------------

  get duration(): number | undefined {
    // in seconds, at the current tempo
    return this.scheduler.durationSec;
  }

  get fileTempo(): number | undefined {
    return this.song?.fileTempo;
  }

  get ticksPerBeat(): number | undefined {
    return this.song?.ticksPerBeat;
  }

  get voices(): Voice[] {
    return this.song?.voices ?? [];
  }

  getCurrentPercent(): number | undefined {
    if (!this.song) {
      return;
    }
    return this.scheduler.positionPercent();
  }

  // Inside the tap that plays, where iOS needs the session activated and the context resumed
  play() {
    if (!this.song || this.destroyed) {
      return;
    }
    this.stopFirstNote();
    this.audioSession.activate();
    const context = this.ensureGraph();
    this.resume(context);
    this.modulation = this.modulationHalfSteps();
    this.applyVoiceValues();
    this.playing = true;
    this.scheduler.play();
    this.startBackstop();
    playNowPlayingElement();
    setMediaSessionPlaybackState('playing');
    this.reportPosition();
  }

  pause() {
    this.stopFirstNote();
    this.playing = false;
    this.scheduler.pause();
    this.stopBackstop();
    pauseNowPlayingElement();
    setMediaSessionPlaybackState('paused');
    this.reportPosition();
    this.audioSession.rest();
  }

  restart() {
    this.scheduler.seek(0);
    this.reportPosition();
    this.updateLocationSlider?.();
  }

  setNewTime(value: number) {
    this.seekToPercent(value);
  }

  setLocationPercentage(percentage: number) {
    this.seekToPercent(percentage);
  }

  setLoop(loop: boolean) {
    this.scheduler.setLoop(loop);
  }

  // The mixer's voice values are applied in place; a transposition re-schedules what is to come
  onVolumeChange() {
    this.applyVoiceValues();
    const modulation = this.modulationHalfSteps();
    if (modulation !== this.modulation) {
      this.modulation = modulation;
      this.scheduler.reschedule();
    }
  }

  onTempoChange() {
    const elapsedMs = performance.now() - this.tempoAppliedAtMs;
    if (elapsedMs < tempoApplyIntervalMs) {
      if (this.tempoApplyTimer === undefined) {
        this.tempoApplyTimer = setTimeout(() => {
          this.tempoApplyTimer = undefined;
          this.applyTempo();
        }, tempoApplyIntervalMs - elapsedMs);
      }
      return;
    }
    this.applyTempo();
  }

  private applyTempo() {
    this.tempoAppliedAtMs = performance.now();
    this.scheduler.setRate(this.rateForCurrentTempo());
    this.onDurationChange?.(this.duration);
    this.reportPosition();
  }

  // The first note of `voice` from `startTick` on, as the MP3 first-note player plays it: at the
  // first-note tempo, at full volume whatever the mixer says, straight to the master
  playFirstNote(voice: Voice, startTick?: number) {
    this.stopFirstNote();
    const song = this.song;
    if (!this.midiData || !song || this.destroyed) {
      return;
    }
    const firstNoteMidi = getFirstNoteMidi(this.midiData, voice, startTick);
    const events = firstNoteMidi?.tracks[0] ?? [];
    const noteOn = events.find((event): event is MidiNoteOnEvent => event.type === 'noteOn');
    const noteOff = events.find((event): event is MidiNoteOffEvent => event.type === 'noteOff');
    if (!noteOn || !noteOff) {
      return;
    }
    const durationSec = (noteOff.deltaTime / song.ticksPerBeat / firstNoteTempo) * 60;
    this.audioSession.activate();
    const context = this.ensureGraph();
    this.resume(context);
    this.firstNote = this.soundNote({
      noteNumber: noteOn.noteNumber + this.modulationHalfSteps(),
      gain: 1,
      whenSec: context.currentTime + 0.05,
      durationSec,
      output: this.master!,
    });
  }

  destroy() {
    // stop music when back button is pressed
    this.pause();
    this.destroyed = true;
    setMediaSessionPosition(undefined); // the next song's player reports its own
    if (this.tempoApplyTimer !== undefined) {
      clearTimeout(this.tempoApplyTimer);
      this.tempoApplyTimer = undefined;
    }
    this.master?.disconnect();
    this.limiter?.disconnect();
    this.ceiling?.disconnect();
    for (const gain of this.voiceGains.values()) {
      gain.disconnect();
    }
    this.voiceGains.clear();
  }

  // ---- loading --------------------------------------------------------------------------------

  private async load() {
    // A player destroyed while loading (the next song came first) reports nothing: the error would
    // land on the Sequencer that now owns another player
    let buffer: ArrayBuffer;
    try {
      const response = await fetch(this.midiFilePath);
      if (!response.ok) {
        // an expected transient, not an error - see onNetworkError in sequencer.tsx (issue #3265)
        console.log(`Error fetching MIDI file ${this.midiFilePath}: ${response.statusText}`);
        this.reportLoadError(NetworkError.MidiDownloadFailed);
        return;
      }
      buffer = await response.arrayBuffer();
    } catch (error: unknown) {
      console.log(`Error fetching MIDI file ${this.midiFilePath}`, error);
      this.reportLoadError(NetworkError.MidiDownloadFailed);
      return;
    }
    let song: MidiSong | undefined;
    let midiData: MidiData;
    try {
      midiData = parseMidi(new Uint8Array(buffer));
      song = getMidiSong(midiData);
    } catch (error: unknown) {
      console.error(`Error parsing MIDI file ${this.midiFilePath}`, error);
      this.reportLoadError(NetworkError.MidiFileParseFailed);
      return;
    }
    if (!song) {
      console.error(`Error parsing MIDI file ${this.midiFilePath}: no ticksPerBeat in the header`);
      this.reportLoadError(NetworkError.MidiFileParseFailed);
      return;
    }
    // the notes cannot sound without the soundfont; a failed load is reported as the MP3 path
    // reports a render it could not make
    const instrumentLoaded = await loadInstrument();
    if (this.destroyed) {
      return;
    }
    if (!instrumentLoaded) {
      this.reportLoadError(NetworkError.MP3GenerationFailed);
      return;
    }
    this.midiData = midiData;
    this.song = song;
    this.scheduler.setSong(song);
    this.scheduler.setRate(this.rateForCurrentTempo());
    this.onDurationChange?.(this.duration);
    this.onVoiceChange?.(song.voices);
    this.setSongReady();
  }

  private reportLoadError(networkError: NetworkError) {
    if (this.destroyed) {
      return;
    }
    this.onNetworkError(networkError);
  }

  // The tempo the listener chose over the file's own: the scheduler's rate
  private rateForCurrentTempo(): number {
    const tempo = this.getCurrentTempo();
    const fileTempo = this.song?.fileTempo;
    if (!tempo || !fileTempo) {
      return 1;
    }
    return tempo / fileTempo;
  }

  private seekToPercent(percent: number) {
    const song = this.song;
    if (!song) {
      console.error('Setting the position when no song is loaded');
      return;
    }
    this.scheduler.seek((percent / 100) * song.durationSec);
    this.reportPosition();
  }

  // The player owns the position; the Sequencer owns the rest of the Media Session (the metadata,
  // the action handlers). Reported wherever the position or the rate moves other than by playing
  // on: a play, a pause, a seek, a tempo change, the start of the next verse.
  private reportPosition() {
    setMediaSessionPosition(this.scheduler.nowPlayingPosition());
  }

  // The end of a verse on the audio clock: the Sequencer counts verses and sets the loop for the
  // next boundary (setLoop), or restarts, stops or moves on. With the loop off the song is over,
  // and the player stops as the audio element did on 'ended': what the Sequencer does next (a
  // restart for the next song, a play for a repeat) must not sound this song's opening again.
  private onVerseEnd() {
    if (!this.playing) {
      return;
    }
    if (!this.scheduler.isLooping) {
      this.playing = false;
      this.scheduler.pause();
      this.stopBackstop();
    }
    this.reportPosition(); // the next verse begins at 0, or the song ends at the end
    this.endOfFile();
  }

  // ---- the graph ------------------------------------------------------------------------------

  private ensureGraph(): AudioContext {
    if (this.context && this.master) {
      return this.context;
    }
    const context = this.getAudioContext();
    this.context = context;
    const ceiling = context.createGain();
    ceiling.gain.value = ceilingGain;
    ceiling.connect(context.destination);
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = limiterSettings.threshold;
    limiter.knee.value = limiterSettings.knee;
    limiter.ratio.value = limiterSettings.ratio;
    limiter.attack.value = limiterSettings.attack;
    limiter.release.value = limiterSettings.release;
    limiter.connect(ceiling);
    const master = context.createGain();
    master.gain.value = masterGain;
    master.connect(limiter);
    this.ceiling = ceiling;
    this.limiter = limiter;
    this.master = master;
    return context;
  }

  // Synchronously inside the gesture; a running context is untouched, and a refusal (a play that
  // lands outside a gesture on iOS) is logged rather than thrown at the caller
  private resume(context: AudioContext) {
    if (context.state === 'running') {
      return;
    }
    context.resume().catch((error: unknown) => {
      console.error('Web Audio player: resuming the audio context failed', error);
    });
  }

  private voiceGain(voice: Voice): GainNode {
    let gain = this.voiceGains.get(voice);
    if (!gain) {
      const context = this.ensureGraph();
      gain = context.createGain();
      gain.gain.value = getVoiceValue(voice, this.getVoiceValues());
      gain.connect(this.master!);
      this.voiceGains.set(voice, gain);
    }
    return gain;
  }

  private applyVoiceValues() {
    const context = this.context;
    if (!context) {
      return;
    }
    const voiceValues = this.getVoiceValues();
    for (const [voice, gain] of this.voiceGains) {
      const value = getVoiceValue(voice, voiceValues);
      gain.gain.setTargetAtTime(value, context.currentTime, voiceGainSmoothingSec);
    }
  }

  // A soundfont note on the context: its sample through a gain of its own, ramped in over the
  // attack, held for `durationSec` (or until the sample is about to end, whichever is sooner) and
  // then let go over the release
  private soundNote({
    noteNumber,
    gain: level,
    whenSec,
    durationSec,
    output,
  }: {
    noteNumber: number;
    gain: number;
    whenSec: number;
    durationSec: number;
    output: AudioNode;
  }): ScheduledNoteHandle {
    const noteName = getMidiNoteName(noteNumber);
    const buffer = noteName ? getSoundfontBuffer(noteName) : undefined;
    const context = this.context;
    if (!buffer || !context) {
      return { cancel: () => undefined }; // outside the soundfont's 88 keys: the MP3 path has no sample either
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    const gainNode = context.createGain();
    const gain = Math.max(level, envelopeFloor);
    const holdSec = Math.max(noteAttackSec, Math.min(durationSec, buffer.duration - releaseSec - sampleEndMarginSec));
    const releaseStartSec = whenSec + holdSec;
    const stopSec = releaseStartSec + releaseSec;
    gainNode.gain.setValueAtTime(0, whenSec);
    gainNode.gain.linearRampToValueAtTime(gain, whenSec + noteAttackSec);
    gainNode.gain.setValueAtTime(gain, releaseStartSec);
    gainNode.gain.exponentialRampToValueAtTime(envelopeFloor, stopSec);
    source.connect(gainNode);
    gainNode.connect(output);
    source.onended = () => {
      source.disconnect();
      gainNode.disconnect();
    };
    source.start(whenSec);
    source.stop(stopSec + 0.01);
    return {
      cancel: () => {
        const now = context.currentTime;
        if (now < whenSec) {
          // not begun: it never sounds
          source.onended = null;
          source.disconnect();
          gainNode.disconnect();
          stopSource(source, now);
          return;
        }
        // sounding: a quick fade from wherever its gain is, then stopped
        const automation = gainNode.gain;
        automation.cancelScheduledValues(now);
        automation.setValueAtTime(Math.max(automation.value, envelopeFloor), now);
        automation.exponentialRampToValueAtTime(envelopeFloor, now + stopFadeSec);
        stopSource(source, now + stopFadeSec + 0.01);
      },
    };
  }

  private stopFirstNote() {
    this.firstNote?.cancel();
    this.firstNote = undefined;
  }

  // What the scheduler plays through: notes as nodes on the context, wakes as silent sources whose
  // `ended` fires from the audio clock
  private sink(): SongSink {
    return {
      now: () => this.context?.currentTime ?? 0,
      play: (note: MidiNote, whenSec: number, durationSec: number) =>
        this.soundNote({
          noteNumber: note.noteNumber + this.modulation,
          gain: note.velocity,
          whenSec,
          durationSec,
          output: this.voiceGain(note.voice),
        }),
      at: (whenSec: number, callback: () => void) => this.wakeAt(whenSec, callback),
    };
  }

  private wakeAt(whenSec: number, callback: () => void): () => void {
    const context = this.ensureGraph();
    this.wakeBuffer ??= context.createBuffer(1, 128, context.sampleRate); // one silent render quantum
    const source = context.createBufferSource();
    source.buffer = this.wakeBuffer;
    source.connect(context.destination);
    let cancelled = false;
    source.onended = () => {
      source.disconnect();
      if (!cancelled) {
        callback();
      }
    };
    source.start(Math.max(whenSec, context.currentTime));
    return () => {
      if (cancelled) {
        return;
      }
      cancelled = true;
      source.onended = null;
      source.disconnect();
      stopSource(source);
    };
  }

  // A timer that tops the scheduled stretch up should the audio clock's wake not arrive; nothing
  // to add costs nothing
  private startBackstop() {
    this.stopBackstop();
    this.backstop = setInterval(() => this.scheduler.fill(), backstopIntervalMs);
  }

  private stopBackstop() {
    if (this.backstop !== undefined) {
      clearInterval(this.backstop);
      this.backstop = undefined;
    }
  }
}
